/**
 * Cliente REST do contestacao-service.
 *
 * Como o resto do Financeiro (faturamento-api), a contestação é
 * consumida direto do serviço — decisão de 09/08/2026. O token é o mesmo do Keycloak (realm
 * `generator`); o serviço resolve o gerador a partir dele, então nenhuma chamada envia
 * `generator_id`.
 */

import { getValidToken } from '@/integrations/keycloak/client';

const API_URL = import.meta.env.VITE_CONTESTACAO_API_URL;

export type ContestationObjectType = 'COLLECT' | 'TRANSACTION' | 'BILLING_CYCLE';

/** Natureza do problema. Os valores permitidos dependem do `object_type`. */
export type ContestationType =
  | 'EXISTENCE'
  | 'DATA'
  | 'VALUE'
  | 'INCORRECT_VALUE'
  | 'UNDUE_TRANSACTION'
  | 'INCORRECT_TOTAL'
  | 'UNDUE_BILLING';

export type ContestationStatus =
  | 'OPEN'
  | 'IN_ANALYSIS'
  | 'AWAITING_INFO'
  | 'ACCEPTED'
  | 'PARTIALLY_ACCEPTED'
  | 'REJECTED'
  | 'CORRECTION_PENDING'
  | 'CORRECTION_APPLIED'
  /** Venceu sem conclusão dentro do prazo (EDD-623). Estado final, **sem decisão** — não é rejeição. */
  | 'EXPIRED';

/** Tipos de evidência aceitos. Vídeo ficou fora do v0 (RFC, 09/08/2026). */
export const ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;

export type AttachmentContentType = (typeof ATTACHMENT_CONTENT_TYPES)[number];

export const MAX_ATTACHMENT_SIZE_BYTES = 10_485_760; // 10MB — igual ao limite do serviço

/**
 * Prazo para contestar, em dias corridos contados da publicação do ciclo (RFC RN-01).
 *
 * Espelha um valor chumbado no serviço, que é quem realmente barra a abertura — aqui ele só evita
 * oferecer uma ação que voltaria com `409`.
 */
export const CONTESTATION_WINDOW_DAYS = 30;

/** Autor de uma entrada da conversa. `OPERATOR` é a Musa; `GENERATOR`, o próprio gerador. */
export type MessageAuthorType = 'OPERATOR' | 'GENERATOR';

/** Uma entrada da conversa da contestação (RFC DT-11). */
export interface ContestationMessage {
  id: string;
  author_type: MessageAuthorType;
  author_id: string;
  content: string;
  created_at: string;
}

/**
 * A decisão registrada pela Musa. `corrections` é objeto solto no serviço — as três chaves
 * possíveis vêm como string decimal, porque o domínio é financeiro.
 */
export interface ContestationResolution {
  id: string;
  resolution_reason: string;
  corrections: {
    units_collected?: number | string | null;
    weight_collected?: number | string | null;
    value?: number | string | null;
    status?: string | null;
  } | null;
  resolved_by: string;
  resolved_at: string;
}

export interface Contestation {
  id: string;
  object_type: ContestationObjectType;
  object_id: string;
  contestation_type: ContestationType;
  status: ContestationStatus;
  generator_id: number;
  initiated_by: string;
  /**
   * Nome e e-mail de quem abriu, das claims do token na abertura. Nulos em contestação aberta
   * antes das colunas existirem — a tela cai para o rótulo genérico.
   */
  initiated_by_name?: string | null;
  initiated_by_email?: string | null;
  initiated_by_realm: 'generator' | 'musa';
  reason: string;
  response_deadline: string | null;
  /**
   * Prazo para a Musa concluir: 3 dias úteis, vence às 23:59:59 de Brasília (EDD-623). Distinto de
   * `response_deadline`, que é só o SLA da fila. Nulo em contestação aberta antes do prazo existir —
   * ela não vence sozinha.
   */
  conclusion_deadline?: string | null;
  /** Quando a contestação venceu. Só preenchido em `EXPIRED`. */
  expired_at?: string | null;
  billing_blocked: boolean;
  /**
   * Ciclos publicados do objeto no instante da abertura — o vínculo com a medição que **não**
   * depende do objeto continuar aparecendo em tela.
   *
   * São todos os ciclos resolvidos, incluindo o já faturado, e não apenas os que receberam
   * `hold`. Vem vazio em contestação aberta antes da coluna existir.
   */
  billing_cycle_ids: number[];
  /**
   * O objeto como estava na abertura — o lado "de" de "corrigido de X para Y".
   *
   * Mesmas chaves de `ContestationResolution.corrections`, de propósito: o histórico cruza os
   * dois por chave. Nulo em contestação aberta antes da coluna existir, e nulo quando o sistema
   * de origem não devolveu nenhum dos campos.
   */
  contested_snapshot?: {
    units_collected?: number | string | null;
    weight_collected?: number | string | null;
    value?: number | string | null;
  } | null;
  /**
   * A conversa, em ordem cronológica. **Só o detalhe traz** — na listagem o serviço devolve
   * `null`, que é "não consultado", diferente de `[]`, que é "ninguém escreveu".
   */
  messages?: ContestationMessage[] | null;
  /** A decisão, quando existe. `null` também na listagem, que não consulta. */
  resolution?: ContestationResolution | null;
  created_at: string;
  updated_at: string;
}

export interface ContestationAttachment {
  id: string;
  file_name: string;
  content_type: AttachmentContentType;
  file_size_bytes: number;
  uploaded_by: string;
  created_at: string;
}

export interface ContestationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Contestation[];
}

export interface CreateContestationInput {
  object_type: ContestationObjectType;
  object_id: string;
  contestation_type: ContestationType;
  reason: string;
}

/**
 * Erro da API de contestação, com o código de negócio preservado.
 *
 * As recusas de abertura chegam como `409` e o `code` diz qual regra barrou — prazo vencido,
 * contestação ativa para o mesmo objeto, ciclo ainda não publicado ou exclusão mútua entre
 * fatura e transação do mesmo ciclo. A tela usa o código para escolher a mensagem.
 */
export class ContestacaoApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'ContestacaoApiError';
  }

  /** Gerador tentando agir sobre contestação que não é dele — ver DT de escopo multi-gerador. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface ApiErrorBody {
  code?: string;
  detail?: string;
  message?: string;
}

function messageFromBody(body: ApiErrorBody | null, status: number): string {
  return body?.detail ?? body?.message ?? `Erro ${status} no serviço de contestação`;
}

async function request<T>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  const token = await getValidToken();
  const res = await fetch(`${API_URL}/v1${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: init?.body != null ? JSON.stringify(init.body) : undefined,
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new ContestacaoApiError(
      res.status,
      body?.code ?? null,
      messageFromBody(body, res.status),
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface ListContestationsFilter {
  status?: ContestationStatus;
  object_type?: ContestationObjectType;
  page?: number;
  page_size?: number;
}

export async function fetchContestations(
  filter?: ListContestationsFilter,
): Promise<ContestationListResponse> {
  const params = new URLSearchParams();
  if (filter?.status) params.set('status', filter.status);
  if (filter?.object_type) params.set('object_type', filter.object_type);
  if (filter?.page) params.set('page', String(filter.page));
  if (filter?.page_size) params.set('page_size', String(filter.page_size));

  const qs = params.toString();
  return request<ContestationListResponse>(
    `/contestations${qs ? `?${qs}` : ''}`,
  );
}

export async function createContestation(
  input: CreateContestationInput,
): Promise<Contestation> {
  return request<Contestation>('/contestations', {
    method: 'POST',
    body: input,
  });
}

export async function fetchContestationAttachments(
  contestationId: string,
): Promise<ContestationAttachment[]> {
  return request<ContestationAttachment[]>(
    `/contestations/${contestationId}/attachments`,
  );
}

/**
 * Detalhe de uma contestação — é o **único** lugar de onde saem `messages` e `resolution`.
 *
 * A listagem não calcula os dois de propósito (seria uma query por linha), e sem eles o gerador
 * abria a contestação, a Musa respondia, e a resposta não aparecia em lugar nenhum do Portal: a
 * única evidência era o status mudando.
 */
export async function fetchContestation(
  contestationId: string,
): Promise<Contestation> {
  return request<Contestation>(`/contestations/${contestationId}`);
}

export interface ContestationAttachmentDownload {
  download_url: string;
  expires_in_seconds: number;
  file_name: string;
  content_type: string;
}

/**
 * URL assinada e temporária do anexo. O bucket é privado — não existe URL permanente, então o
 * link é pedido no clique e usado na hora.
 */
export async function fetchContestationAttachmentDownload(
  contestationId: string,
  attachmentId: string,
): Promise<ContestationAttachmentDownload> {
  return request<ContestationAttachmentDownload>(
    `/contestations/${contestationId}/attachments/${attachmentId}/download`,
  );
}

/** Gerador responde a uma solicitação de informações (status `AWAITING_INFO`). */
export async function supplementContestation(
  contestationId: string,
  message: string,
): Promise<void> {
  await request<void>(`/contestations/${contestationId}/supplement`, {
    method: 'POST',
    body: { message },
  });
}

/**
 * Anexa evidência em duas etapas: o serviço registra o metadado e devolve uma URL assinada,
 * e o arquivo vai direto para o S3 — não passa pela API.
 */
export async function uploadContestationAttachment(
  contestationId: string,
  file: File,
): Promise<string> {
  const { upload_url, attachment_id } = await request<{
    upload_url: string;
    attachment_id: string;
  }>(`/contestations/${contestationId}/attachments`, {
    method: 'POST',
    body: {
      file_name: file.name,
      content_type: file.type,
      file_size_bytes: file.size,
    },
  });

  const upload = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!upload.ok) {
    throw new ContestacaoApiError(
      upload.status,
      'attachment_upload_failed',
      'Não foi possível enviar o arquivo de evidência.',
    );
  }

  return attachment_id;
}
